import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const FeaturesSection = () => {
  const [visible, setVisible] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          [0,1,2,3].forEach((i) => setTimeout(() => setVisible(v => [...v, i]), i * 120));
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const features = [
    {
      id: 'reframe',
      icon: 'Brain',
      title: 'Mind Reframing',
      description: 'Replace negative loops by installing new thoughts that feel believable in your own voice.',
      color: 'from-violet-500/20 to-purple-500/20'
    },
    {
      id: 'voice',
      icon: 'Mic',
      title: 'Your Voice as Guide',
      description: 'Guidance delivered in your own cloned voice. Familiar. Trustworthy. Effective.',
      color: 'from-blue-500/20 to-cyan-500/20'
    },
    {
      id: 'rituals',
      icon: 'RefreshCw',
      title: 'Personalized Daily Rituals',
      description: 'Built around your real situation. Four guides. Every day. Repeated until it sticks.',
      color: 'from-emerald-500/20 to-green-500/20'
    },
    {
      id: 'security',
      icon: 'Shield',
      title: 'Zero-Knowledge Privacy',
      description: 'Entries encrypted before they leave your device. Not even we can read them.',
      color: 'from-amber-500/20 to-orange-500/20'
    }
  ];

  return (
    <section id="features" ref={sectionRef} className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img src="/image2.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-[0.12]" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-transparent to-background/30" />
      </div>
      <div className="absolute inset-0 cosmic-gradient opacity-15 pointer-events-none" />
      
      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            What DepthMe helps you with
          </h2>
        </div>

        {/* Features Grid - 2x2 */}
        <div className="grid md:grid-cols-2 gap-6">
          {features?.map((feature) => (
            <div
              key={feature?.id}
              className={`glassmorphism p-8 rounded-2xl hover:scale-105 transition-all duration-500 group ${visible.includes(features.indexOf(feature)) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${features.indexOf(feature) * 120}ms` }}
            >
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature?.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <Icon name={feature?.icon} size={24} className="text-primary" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-foreground mb-3">
                {feature?.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {feature?.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;